import { aesGcmDecrypt } from '../crypto/crypto'
import { db, type StoredMutation } from '../platform/webStorage'

// The page's saved versions: the list the server keeps (labels encrypted),
// reconciled with the copies on this device, and snapshot blobs decrypted.

export interface VersionEntry {
  id: string
  timestamp: number
  wordCount: number
  label: string
}

export interface ServerVersionRow {
  id: string
  version_number: number
  byte_size: number
  word_count: number
  label_encrypted: string | null
  label_nonce: string | null
  epoch: number
  created_at: string
}

/** The versions kept on this device, when the platform keeps any. */
export async function localVersionList(docId: string): Promise<VersionEntry[]> {
  if (typeof window !== 'undefined' && (window as any).api?.listHistory) {
    return (window as any).api.listHistory(docId)
  }
  return []
}

/** The server's versions, with their labels decrypted. */
export async function decodeServerVersions(versions: ServerVersionRow[], masterKey: CryptoKey): Promise<VersionEntry[]> {
  const serverVersions: VersionEntry[] = []

  for (const v of versions) {
    let label = 'Snapshot'
    if (v.label_encrypted && v.label_nonce) {
      try {
        const decBytes = await aesGcmDecrypt(masterKey, {
          ciphertext: v.label_encrypted,
          iv: v.label_nonce,
        })
        label = new TextDecoder().decode(decBytes) || 'Snapshot'
      } catch {
        label = 'Snapshot'
      }
    }

    const rawDate = v.created_at || ''
    const timestamp = Date.parse(rawDate.includes('T') ? rawDate : rawDate.replace(' ', 'T') + 'Z') || Date.now()

    serverVersions.push({
      id: v.id,
      timestamp,
      wordCount: v.word_count || 0,
      label,
    })
  }
  return serverVersions
}

/**
 * The server's list with this device's unsent snapshots added, newest first.
 * Local copies the server has since dropped (its retention keeps 50) are
 * pruned along the way.
 */
export async function reconcileWithLocalVersions(docId: string, serverVersions: VersionEntry[]): Promise<VersionEntry[]> {
  if (typeof window !== 'undefined' && (window as any).api?.listHistory) {
    try {
      const localList: VersionEntry[] = await (window as any).api.listHistory(docId)
      const serverIds = new Set(serverVersions.map((s) => s.id))

      let pendingIds = new Set<string>()
      try {
        if (typeof db !== 'undefined' && db.outbox) {
          const pendingSnapshots = await db.outbox.where('type').equals('snapshot').toArray()
          pendingIds = new Set(pendingSnapshots.map((m: StoredMutation) => m.targetId))
        }
      } catch {}

      const toPrune = localList.filter((l) => !serverIds.has(l.id) && !pendingIds.has(l.id))
      if (toPrune.length > 0 && serverVersions.length > 0) {
        if (typeof db !== 'undefined' && db.snapshots) {
          await db.snapshots.bulkDelete(toPrune.map((p) => p.id)).catch(() => {})
        }
      }

      const pendingItems = localList.filter((l) => pendingIds.has(l.id))
      const combined = [...pendingItems, ...serverVersions.filter((s) => !pendingIds.has(s.id))]
      combined.sort((a, b) => b.timestamp - a.timestamp)
      return combined
    } catch {}
  }

  return serverVersions
}

/** A snapshot blob's text. The packed format is [12-byte IV][AES-GCM ciphertext + 16-byte tag]. */
export async function decryptSnapshotBlob(blobBytes: Uint8Array, key: CryptoKey): Promise<string> {
  const iv = blobBytes.slice(0, 12)
  const ciphertext = blobBytes.slice(12)
  const plainBuf = await crypto.subtle.decrypt(
    { name: 'AES-GCM', iv },
    key,
    ciphertext
  )
  return new TextDecoder().decode(plainBuf)
}

/** A snapshot's text, encrypted for upload in the same packed format. */
export async function packSnapshotBlob(content: string, key: CryptoKey): Promise<Uint8Array<ArrayBuffer>> {
  const iv = crypto.getRandomValues(new Uint8Array(12))
  const cipherBuf = await crypto.subtle.encrypt(
    { name: 'AES-GCM', iv },
    key,
    new TextEncoder().encode(content)
  )
  const packed = new Uint8Array(12 + cipherBuf.byteLength)
  packed.set(iv, 0)
  packed.set(new Uint8Array(cipherBuf), 12)
  return packed
}
